import { z } from 'zod'

export const baseSearchSchema = z.object({
	page: z.number().int().positive().default(1),
	limit: z.number().int().positive().max(100).default(20),
	sort: z.string().optional(),
	order: z.enum(['asc', 'desc']).default('desc'),
	search: z.string().optional(),
	filters: z.string().optional(),
	tags: z.array(z.string()).optional(),
	status: z.string().optional(),
})

export const deviceSearchSchema = baseSearchSchema.extend({
	metadata: z.record(z.string(), z.union([z.string(), z.array(z.string())])).optional(),
	hardwareVersion: z.string().optional(),
	firmwareVersion: z.string().optional(),
	lastSeen: z.string().optional(),
	// Grouped filter bar state (LZString-encoded FilterGroup[]); separate from the legacy `filters` param.
	groups: z.string().optional(),
})

export const streamsSearchSchema = baseSearchSchema.extend({
	streamType: z.string().optional(),
	// Grouped filter bar state (LZString-encoded FilterGroup[]); separate from the legacy `filters` param.
	groups: z.string().optional(),
})

export const dashboardSearchSchema = baseSearchSchema.extend({
	category: z.string().optional(),
	shared: z.boolean().optional(),
})

export const sessionRulesSearchSchema = baseSearchSchema.extend({
	severity: z.enum(['low', 'medium', 'high', 'critical']).optional(),
	enabled: z.boolean().optional(),
})

export const dbcParsersSearchSchema = baseSearchSchema.extend({
	version: z.string().optional(),
	protocol: z.string().optional(),
})

export const subscriptionPlansSearchSchema = z.object({
	openPlansModal: z.preprocess(val => val === 'true' || val === true, z.boolean()).optional(),
})

export type BaseSearch = z.infer<typeof baseSearchSchema>
export type DeviceSearch = z.infer<typeof deviceSearchSchema>
export type DashboardSearch = z.infer<typeof dashboardSearchSchema>
export type SessionRulesSearch = z.infer<typeof sessionRulesSearchSchema>
export type DbcParsersSearch = z.infer<typeof dbcParsersSearchSchema>
export type SubscriptionPlansSearch = z.infer<typeof subscriptionPlansSearchSchema>

export const ACCESS_DENIED_ORG_OWNER_ONLY_REASON = 'org-owner-only' as const
export const ACCESS_DENIED_NO_PROJECT_ACCESS_REASON = 'no-project-access' as const
export const ACCESS_DENIED_PROJECT_SETTINGS_ADMIN_ONLY_REASON = 'project-settings-admin-only' as const

export const accessDeniedSearchSchema = z.object({
	reason: z
		.union([
			z.literal(ACCESS_DENIED_ORG_OWNER_ONLY_REASON),
			z.literal(ACCESS_DENIED_NO_PROJECT_ACCESS_REASON),
			z.literal(ACCESS_DENIED_PROJECT_SETTINGS_ADMIN_ONLY_REASON),
		])
		.optional(),
})

export type AccessDeniedSearch = z.infer<typeof accessDeniedSearchSchema>
